import { FC, useState } from "react";

import { Box, Stack } from "@mui/material";
import { Locales } from "@simplyk/common";

import EasyEn from "../../../public/images/sticker/easy_en.svg";
import EasyFr from "../../../public/images/sticker/easy_fr.svg";

import { CloseDialogButton } from "./CloseDialogButton";
import { EmbedChoice } from "./enums";
import { ShareFormTabLayout } from "./ShareFormTabLayout";
import { ShareFormTabTitle } from "./ShareFormTabTitle";

import { ButtonTabs } from "@/components/ButtonTabs/ButtonTabs";
import Image from "@/components/Image/Image";
import { useLocaleContext } from "@/contexts/LocaleContext";
import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { ChevronLeft, ChevronRight } from "@/icons/outlined";

const gifSrcByTab = {
  ["Form"]: "/gifs/form-embed.gif",
  ["ButtonPopup"]: "/gifs/popup-embed.gif",
  ["Thermometer"]: "/gifs/thermometer.gif",
  ["Leaderboard"]: "/gifs/leaderboard.gif",
  ["LinkedButton"]: "/gifs/linked-button.gif",
};

type ChooseEmbedTypeTabProps = {
  availableEmbedChoices: EmbedChoice[];
  defaultChoice?: EmbedChoice;
  onClose?: () => void;
  onBackClick?: () => void;
  onChooseClick?: (choice: EmbedChoice) => void;
};

export const ChooseEmbedTypeTab: FC<ChooseEmbedTypeTabProps> = ({
  availableEmbedChoices,
  defaultChoice,
  onClose,
  onBackClick,
  onChooseClick,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { isSmallScreen } = useMediaQuery();

  const [tab, setTab] = useState<EmbedChoice>(defaultChoice ?? (availableEmbedChoices[0] as EmbedChoice));

  return (
    <ShareFormTabLayout
      slots={{
        closeButton: <CloseDialogButton onClick={onClose} />,
        header: <ShareFormTabTitle>{t("dashboard", "shareFormModal.embed.title")}</ShareFormTabTitle>,
        content: (
          <Stack gap={3}>
            <ButtonTabs
              justifyContent="center"
              activeTab={tab}
              onTabChange={(value) => setTab(value as EmbedChoice)}
              tabs={availableEmbedChoices.map((embedChoice) => ({
                value: embedChoice,
                label: t("dashboard", `shareFormModal.embed.tab.${embedChoice}`),
                sticker: embedChoice === "LinkedButton" && (locale === Locales.EN ? <EasyEn /> : <EasyFr />),
              }))}
            />
            <Stack>
              {!isSmallScreen && (
                <DashboardInfobox
                  vibe="brand"
                  type="alert"
                  size="medium"
                  pointer="bottom"
                  hideIcon
                  title={t("dashboard", `shareFormModal.embed.tab.${tab}.infoBox.title`)}
                >
                  {t("dashboard", `shareFormModal.embed.tab.${tab}.infoBox.content`)}
                </DashboardInfobox>
              )}
              <Box sx={{ textAlign: "center" }}>
                <Image src={gifSrcByTab[tab]} alt="Form embed" width={isSmallScreen ? 260 : 450} height={undefined} />
              </Box>
              {isSmallScreen && (
                <DashboardInfobox
                  vibe="brand"
                  type="alert"
                  size="medium"
                  pointer="top"
                  hideIcon
                  title={t("dashboard", `shareFormModal.embed.tab.${tab}.infoBox.title`)}
                >
                  {t("dashboard", `shareFormModal.embed.tab.${tab}.infoBox.content`)}
                </DashboardInfobox>
              )}
            </Stack>
          </Stack>
        ),
        footer: (
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <DashboardButton
              vibe="neutral"
              variant="outlined"
              size="large"
              startIcon={<ChevronLeft />}
              onClick={onBackClick}
            >
              {t("common", "back")}
            </DashboardButton>
            <DashboardButton
              vibe="brand"
              variant="filled"
              size="large"
              endIcon={<ChevronRight />}
              onClick={() => onChooseClick?.(tab)}
            >
              {t("dashboard", "common.choose")}
            </DashboardButton>
          </Stack>
        ),
      }}
    />
  );
};
